import { types, Instance } from "mobx-state-tree";
import React, { useContext, createContext } from "react";
import { ModsModel } from "./ModsStore";
import { PlayerModel } from "./PlayerStore";
import { PlatformModel } from "./PlatformStore";
import { DynamicDataModel } from "./DynamicDataStore";
import { LayoutModel } from "./layoutStore";
import { TimelineModel } from "./TimelineStore";
import { CanvasTransformModel } from "./CanvasTransformStore";
import { UndoModel } from "./UndoStore";
import { NotificationStore } from "./notification/notificationStore";
import { StoreStatus } from "./notification/StoreState";
import { accountModel } from "./account";
import { ExperimentStore } from "./ExperimentStore";
import { parsePathName, getTabName, StudioTabs } from "../utils/navigation";

const location = parsePathName(window.location.pathname);
const defaultTab: StudioTabs = getTabName(location.tab);

export const RootModel = types
  .model({
    layoutStore: LayoutModel,
    platformStore: PlatformModel,
    modsStore: ModsModel,
    playerStore: PlayerModel,
    dynamicDataStore: DynamicDataModel,
    notificationStore: NotificationStore,
    experimentStore: ExperimentStore,
    accountStore: accountModel,
    timelineStore: TimelineModel,
    canvasTransformStore: CanvasTransformModel,
    undoStore: UndoModel,
    currentTab: defaultTab,
  })
  .views((self) => ({}))
  .actions((self) => ({
    runPlayer() {
      self.playerStore.runPlayer();
    },
    setCurrentTab(tab: StudioTabs) {
      self.currentTab = tab;
    },
  }));

export const rootStore = RootModel.create({
  layoutStore: {},
  modsStore: {},
  playerStore: {},
  platformStore: {},
  dynamicDataStore: {},
  accountStore: {},
  notificationStore: {
    notifications: [],
    unreadNotificationsNumber: 0,
    status: "NOT_INITIALIZED" as StoreStatus,
    isSubscribedToOnCreateNotification: false,
    isSubscribedToOnUpdateNotification: false,
    isSubscribedToOnInstantNotification: false,
  },
  experimentStore: {},
  timelineStore: {},
  canvasTransformStore: {},
  undoStore: {},
});

export const MSTContext = React.createContext(rootStore);

export type RootInstance = Instance<typeof RootModel>;

const RootStoreContext = createContext<null | RootInstance>(null);

export const Provider = RootStoreContext.Provider;

export function useMst(): RootInstance {
  const store = useContext(RootStoreContext);
  if (store === null) {
    throw new Error("Store cannot be null, please add a context provider");
  }
  return store;
}
